import type { Dataset } from '@life/data';
import { ensureLegacyFields, type LegacyLife, type Life } from '@life/engine';
import { SPEEDS, type Speed } from './clock';
import { isPendingMinigame, isQueuedMinigame, type PendingMinigame, type QueuedMinigame } from './minigame';
import { DEFAULT_SETTINGS, GAME_MODES, type Settings } from './settings';

export const SAVE_KEY = 'life.save';
/** Сколько завершённых жизней хранится на «полке». */
export const SHELF_MAX = 10;

export interface SaveV1 {
  v: 1;
  settings: Settings;
  life: Life | null;
  /** Завершённые жизни, не больше SHELF_MAX; новые — в конец. */
  shelf: Life[];
  savedAt: number;
  /** Открытая мини-игра (план 2b, Task 8); нет в старых сохранениях. */
  pendingMinigame?: PendingMinigame | null;
  /** Сыгранный, но ещё не применённый исход (ждёт следующего тика); нет в старых сохранениях. */
  minigameQueued?: QueuedMinigame | null;
}

export type LoadResult = { ok: true; save: SaveV1 } | { ok: false; reason: 'empty' | 'corrupt' | 'version' };

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Доступ к localStorage может бросать в приватном режиме.
    return null;
  }
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

/** Ключи настроек, добавленные позже первых сохранений: при чтении берутся по умолчанию. */
const OPTIONAL_KEYS: readonly (keyof Settings)[] = ['speed', 'gamblingEnabled', 'mode'];

function isSettings(x: unknown): x is Partial<Settings> & Record<string, unknown> {
  if (!isRecord(x)) return false;
  return (Object.keys(DEFAULT_SETTINGS) as (keyof Settings)[])
    .filter((k) => !OPTIONAL_KEYS.includes(k))
    .every((k) => k in x);
}

function isSpeed(x: unknown): x is Speed {
  return typeof x === 'number' && (SPEEDS as readonly number[]).includes(x);
}

/** Дополняет старое сохранение новыми ключами и отбрасывает чужие значения. */
function settingsFrom(raw: Partial<Settings> & Record<string, unknown>): Settings {
  const merged = { ...DEFAULT_SETTINGS, ...raw } as Settings;
  if (!isSpeed(merged.speed)) merged.speed = DEFAULT_SETTINGS.speed;
  if (typeof merged.gamblingEnabled !== 'boolean') merged.gamblingEnabled = DEFAULT_SETTINGS.gamblingEnabled;
  if (!GAME_MODES.includes(merged.mode)) merged.mode = DEFAULT_SETTINGS.mode;
  return merged;
}

function isLife(x: unknown): x is LegacyLife {
  return isRecord(x) && typeof x.seed === 'string' && typeof x.age === 'number';
}

/** Сохранение до этапа 2d (без внешности, энергии, мечты…): недостающие поля дополняет движок, возраст тот же. */
function migrateLife(life: LegacyLife, ds?: Dataset): Life {
  return ds === undefined ? ensureLegacyFields(life) : ensureLegacyFields(life, ds);
}

function parseV1(raw: unknown, ds?: Dataset): SaveV1 | null {
  if (!isRecord(raw)) return null;
  if (!isSettings(raw.settings)) return null;
  if (raw.life !== null && !isLife(raw.life)) return null;
  if (!Array.isArray(raw.shelf) || !raw.shelf.every(isLife)) return null;
  if (typeof raw.savedAt !== 'number') return null;
  // Мини-игра без жизни или битая — просто не восстанавливается.
  const pendingMinigame = raw.life !== null && isPendingMinigame(raw.pendingMinigame) ? raw.pendingMinigame : null;
  const minigameQueued = raw.life !== null && isQueuedMinigame(raw.minigameQueued) ? raw.minigameQueued : null;
  const save: SaveV1 = {
    v: 1,
    settings: settingsFrom(raw.settings),
    life: raw.life === null ? null : migrateLife(raw.life, ds),
    shelf: raw.shelf.slice(-SHELF_MAX).map((l) => migrateLife(l, ds)),
    savedAt: raw.savedAt,
  };
  // Ключи мини-игр — только когда есть что восстанавливать (старые сохранения читаются как прежде).
  if (pendingMinigame !== null) save.pendingMinigame = pendingMinigame;
  if (minigameQueued !== null) save.minigameQueued = minigameQueued;
  return save;
}

/**
 * Читает сохранение. Никогда не бросает: мусор → corrupt, чужая версия → version, нет/недоступно → empty.
 * `ds` — датасет для миграции старых жизней (внешность по региону страны); без него — регион по умолчанию.
 */
export function load(storage: Storage | null = defaultStorage(), ds?: Dataset): LoadResult {
  let text: string | null;
  try {
    text = storage ? storage.getItem(SAVE_KEY) : null;
  } catch {
    return { ok: false, reason: 'empty' };
  }
  if (text === null || text === '') return { ok: false, reason: 'empty' };

  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'corrupt' };
  }
  if (!isRecord(raw) || typeof raw.v !== 'number') return { ok: false, reason: 'corrupt' };
  if (raw.v !== 1) return { ok: false, reason: 'version' };

  const save = parseV1(raw, ds);
  return save ? { ok: true, save } : { ok: false, reason: 'corrupt' };
}

/** Пишет сохранение; полка обрезается до SHELF_MAX последних. Ошибки хранилища глотаются. */
export function save(data: SaveV1, storage: Storage | null = defaultStorage()): void {
  const payload: SaveV1 = { ...data, v: 1, shelf: data.shelf.slice(-SHELF_MAX) };
  try {
    storage?.setItem(SAVE_KEY, JSON.stringify(payload));
  } catch {
    // Квота или приватный режим: игра продолжается без сохранения.
  }
}

export function clear(storage: Storage | null = defaultStorage()): void {
  try {
    storage?.removeItem(SAVE_KEY);
  } catch {
    // см. save
  }
}
